import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { usePersistentWidget } from '../context/PersistentWidgetContext';
import Modal from './Modal';
import WidgetRenderer from './WidgetRenderer';
import { persistentWidgetService } from '../services/persistentWidgetService';

const PersistentWidgetPopup = ({ widgetId }) => {
  const { palette } = useTheme();
  const { 
    openWidgetId, 
    getWidgetById,
    getWidgetStatus,
    closePersistentWidget, 
    completePersistentWidget,
    reviewMode,
    reviewCompletion,
    closeReviewMode,
  } = usePersistentWidget();

  const widget = getWidgetById(widgetId);
  const widgetStatus = getWidgetStatus(widgetId);
  const isOpen = openWidgetId === widgetId;

  const handleWidgetComplete = async () => {
    if (!widget) return;

    // Widgets save their own data, so we just need to update the context state
    // The widget has already called saveCompletion, so we check and update state
    const result = await completePersistentWidget(widgetId, {});
    
    // Close popup after a short delay to show completion (even if already completed)
    setTimeout(() => {
      closePersistentWidget(widgetId);
    }, 1000);
  };

  const formatScheduleText = (schedule) => {
    if (!schedule || !schedule.type) return '';
    
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    switch (schedule.type) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        if (schedule.dayOfWeek !== undefined) {
          return `Weekly - ${dayNames[schedule.dayOfWeek]}`;
        }
        return 'Weekly';
      case 'monthly':
        if (schedule.dayOfMonth !== undefined) {
          return `Monthly - Day ${schedule.dayOfMonth}`;
        }
        return 'Monthly';
      default:
        return schedule.type.charAt(0).toUpperCase() + schedule.type.slice(1);
    }
  };

  if (!isOpen || !widget) {
    return null;
  }

  const handleClose = () => {
    if (reviewMode) {
      closeReviewMode();
    } else {
      closePersistentWidget(widgetId);
    }
  };

  return (
    <Modal 
      visible={isOpen} 
      onClose={handleClose}
      showCloseButton={true}
      backgroundColor={palette.background}
      fullScreen={true}
    >
      <ScrollView 
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={true}
      >
        <View style={styles.container}>
          {/* Widget Content */}
          <View style={styles.widgetContainer}>
            <WidgetRenderer 
              widget={widget} 
              onComplete={handleWidgetComplete}
              compact={false}
              reviewMode={reviewMode}
            />
          </View>

          {/* Footer Info */}
          {widgetStatus.completed && !reviewMode && (
            <View style={[styles.completedBanner, { backgroundColor: palette.surface }]}>
              <Text style={[styles.completedText, { color: palette.primary }]}>
                ✓ Completed for this period
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
    height: '100%',
  },
  scrollContent: {
    padding: 10,
    height: '100%',
    overflow: 'visible',
  },
  container: {
    flex: 1,
    overflow: 'visible',
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  description: {
    fontSize: 16,
    marginBottom: 8,
    lineHeight: 22,
  },
  scheduleText: {
    fontSize: 14,
    fontStyle: 'italic',
    marginTop: 4,
  },
  widgetContainer: {
    marginTop: 10,
    height: '100%',
  },
  completedBanner: {
    padding: 12,
    borderRadius: 8,
    marginTop: 20,
    alignItems: 'center',
  },
  completedText: {
    fontSize: 14,
    fontWeight: '600',
  },
  reviewBanner: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 2,
    alignItems: 'center',
  },
  reviewBannerText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default PersistentWidgetPopup;

